/**
 * 🔒 Environment-aware Logger Utility
 * 
 * This logger replaces console.log statements to prevent
 * sensitive data leaks in production builds.
 * 
 * Usage:
 *   import { logger } from '@/utils/logger';
 *   logger.log('Debug info');
 *   logger.error('Error always logged');
 */

// process.env.NODE_ENV works in both Vite (replaced at build) and Metro/Hermes
// (injected by React Native bundler). Avoids Vite-only `import.meta.env`.
const isDevelopment =
  typeof process !== 'undefined' && process.env && process.env.NODE_ENV !== 'production';

export const logger = {
  /**
   * Log general information (development only)
   */
  log: (...args) => {
    if (isDevelopment) {
      console.log(...args);
    }
  },

  /**
   * Log warnings (development only)
   */
  warn: (...args) => {
    if (isDevelopment) {
      console.warn(...args);
    }
  },

  /**
   * Log errors (always logged, even in production)
   * Use for critical errors that need tracking
   */
  error: (...args) => {
    console.error(...args);
  },

  /**
   * Log info messages (development only)
   */
  info: (...args) => {
    if (isDevelopment) {
      console.info(...args);
    }
  },

  /**
   * Log debug messages with prefix (development only)
   */
  debug: (message, ...args) => {
    if (isDevelopment) {
      console.log(`🐛 DEBUG: ${message}`, ...args);
    }
  },

  /**
   * Log success messages (development only)
   */
  success: (message, ...args) => {
    if (isDevelopment) {
      console.log(`✅ ${message}`, ...args);
    }
  }
};

export default logger;
